import React from 'react';
import {
    ArcElement,
    CategoryScale,
    Chart,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    registerables,
    Title,
    Tooltip
} from 'chart.js';
import { Route, Routes } from 'react-router';
import { BrowserRouter, Navigate, useParams } from 'react-router-dom';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import pkg from '../package.json';

// import AccountsPage from './routes/accounts';
import AccountIndividualPage from './routes/account-individual';
import AddTradePage from './routes/add-trade';
import AdminDashboard from './routes/admin';
import AdminAccountPage from './routes/admin/account'; // You'll create this file
import CalendarPage from './routes/calendar';
import ComparePage from './routes/compare';
import DailyTradeNotes from './routes/daily-trade-notes';
// import DailyTradesPage from './routes/daily-trades';
import Dashboard from './routes/dashboard';
import EquityCurvePage from './routes/equity-curve';
import LayoutMain from './routes/layout';
import FundedAccountsLeaderboardPage from './routes/leaderboard';
import NotFoundRedirect from './routes/not-found';
import NotFoundPage from './routes/not-found/page';
import SettingsPage from './routes/settings';
import SummaryPage from './routes/summary';
import TradeIndividualPage from './routes/trade-individual';
import TradeBookPage from './routes/tradebook';
import TradesPage from './routes/trades';
import { useUserState } from './store';
import { PUBLIC_VIEW_ID_PATTERN } from './utilities/account-ids';
import useAuthentication from './utilities/hooks/auth';

import './App.css';
import './resources/styles/_icons.scss';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            retryDelay: 3000
        }
    }
});

console.log('[version]:', pkg.version);

const PublicViewGuard = () => {
    const { id } = useParams();

    if (!PUBLIC_VIEW_ID_PATTERN.test(id || '')) {
        return <Navigate replace to="/404" />;
    }

    return <LayoutMain />;
};

const AddTradeRoute = () => {
    const { getUser } = useUserState();

    if (getUser().role !== 'admin') {
        return <Navigate replace to="/" />;
    }

    return <AddTradePage />;
};

function App() {
    const isUserSet = useAuthentication(); // Call useAuthentication hook

    // Redirect to '/' only after user is set
    if (!isUserSet) {
        return null; // or loading indicator if needed
    }

    Chart.register(
        ...registerables,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        ArcElement,
        Title,
        Tooltip,
        Legend
    );

    return (
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <Routes>
                        {/* Regular routes */}
                        <Route path="/" element={<LayoutMain />}>
                            <Route index element={<Dashboard />} />
                            <Route path="add-trade" element={<AddTradeRoute />} />
                            <Route path="summary" element={<SummaryPage />} />
                            <Route path="equity-curve" element={<EquityCurvePage />} />
                            <Route path="settings/:tabId?" element={<SettingsPage />} />
                            <Route path="accounts/:id" element={<AccountIndividualPage />} />
                            <Route path="trades" element={<TradesPage />} />
                            <Route path="trades/:tradeId" element={<TradeIndividualPage />} />
                            <Route path="calendar" element={<CalendarPage />} />
                            <Route path="compare" element={<ComparePage />} />
                            <Route path="daily-trade-notes" element={<DailyTradeNotes />} />
                            <Route path="tradebook" element={<TradeBookPage />} />
                        </Route>

                        <Route path="admin" element={<LayoutMain />}>
                            <Route index element={<AdminDashboard />} />
                            <Route path=":accountId" element={<AdminAccountPage />} />
                        </Route>

                        <Route path="/admin/u/:unassignedId" element={<LayoutMain />}>
                            <Route index element={<Dashboard />} />
                            <Route path="summary" element={<SummaryPage />} />
                            <Route path="equity-curve" element={<EquityCurvePage />} />
                            <Route path="trades" element={<TradesPage />} />
                            <Route path="calendar" element={<CalendarPage />} />
                            <Route path="compare" element={<ComparePage />} />
                            <Route path="daily-trade-notes" element={<DailyTradeNotes />} />
                            <Route path="tradebook" element={<TradeBookPage />} />
                        </Route>

                        <Route path="404" element={<NotFoundPage />} />
                        <Route path="leaderboard" element={<FundedAccountsLeaderboardPage />} />

                        {/* Public view routes */}
                        <Route path="/:id" element={<PublicViewGuard />}>
                            <Route index element={<Dashboard />} />
                            <Route path="summary" element={<SummaryPage />} />
                            <Route path="equity-curve" element={<EquityCurvePage />} />
                            <Route path="trades" element={<TradesPage />} />
                            <Route path="calendar" element={<CalendarPage />} />
                            <Route path="compare" element={<ComparePage />} />
                            <Route path="daily-trade-notes" element={<DailyTradeNotes />} />
                            <Route path="tradebook" element={<TradeBookPage />} />
                        </Route>

                        <Route path="*" element={<NotFoundRedirect />} />
                    </Routes>
                </BrowserRouter>
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
        </React.StrictMode>
    );
}

export default App;
